/**
 * StudioHeader — the top of the /studio page (HeaderTitle + HeaderActions +
 * Auth/Collab/CommandPalette buttons + StatusStrip). Extracted from
 * ELPMStudioDAW.jsx during the iter-179 refactor so the main file is
 * smaller and the header can be reused (e.g. on /jam or /sessions pages).
 *
 * This component is a pure passthrough — it owns NO state, just forwards
 * props to the existing HeaderTitle/HeaderActions/StatusStrip children.
 * The wrapping <motion.header> animation lives here so consumers don't
 * have to import framer-motion.
 */
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mic2 } from "lucide-react";
import { HeaderTitle, HeaderActions, StatusStrip } from "@/components/daw/Header";
import NowCookingPill from "@/components/NowCookingPill";
import AuthMenu from "@/components/AuthMenu";
import { CollabIndicator } from "@/components/CollabModal";
import { CommandPaletteHeaderButton } from "@/components/CommandPalette";
import BridgeBadge from "@/components/daw/BridgeBadge";
import ModeSwitch from "@/components/creator/ModeSwitch";

const HEADER_INIT = { opacity: 0, y: -10 };
const HEADER_ANIM = { opacity: 1, y: 0 };

export default function StudioHeader(props) {
  const {
    // HeaderActions
    onUpload, onSave, onExport, onLyrics, onSong, onLibrary, onMidi, onDrum,
    onBass, onJsfx, onBackingVocals, onLoops, onJam, onSuno, onLoopStation,
    onMtEditor, onRecordings, onShare,
    busyJam, busyExport, headerUploadRef, onFilePicked,
    // Collab indicator
    collabStatus, collabPeers, onCollabClick,
    // CommandPalette
    onCommandPaletteOpen,
    // StatusStrip
    engineState, playing, loadedCount, maxDur, recordingIdx,
    tempo, musicalKey, dryMode, metronomeOn,
    onEngineClick, onTempoChange, onKeyChange, onLoadedClick, onLengthClick,
    onRecClick, onToggleDryMode, onToggleMetronome,
    // iter-202 — aggregate clip counter.
    clipCount = 0,
    onClipPillClick,
  } = props;

  return (
    <motion.header
      initial={HEADER_INIT}
      animate={HEADER_ANIM}
      className="glass-card mb-5 overflow-hidden rounded-[28px] p-5"
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <HeaderTitle />
        <HeaderActions
          onUpload={onUpload}
          onSave={onSave}
          onExport={onExport}
          onLyrics={onLyrics}
          onSong={onSong}
          onLibrary={onLibrary}
          onMidi={onMidi}
          onDrum={onDrum}
          onBass={onBass}
          onJsfx={onJsfx}
          onBackingVocals={onBackingVocals}
          onLoops={onLoops}
          onJam={onJam}
          onSuno={onSuno}
          onLoopStation={onLoopStation}
          onMtEditor={onMtEditor}
          onRecordings={onRecordings}
          onShare={onShare}
          busyJam={busyJam}
          busyExport={busyExport}
          headerUploadRef={headerUploadRef}
          onFilePicked={onFilePicked}
        />
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <Link to="/create/studio" data-testid="header-rec-studio-btn" title="Guided mic setup + record over music"
          className="inline-flex h-8 items-center gap-1.5 rounded-2xl border border-rose-400/40 bg-rose-500/[0.14] px-3 text-[12.5px] text-rose-50 transition-colors hover:bg-rose-500/[0.24]">
          <Mic2 className="h-3.5 w-3.5" /> Recording Studio
        </Link>
        <NowCookingPill />
        <BridgeBadge />
        <ModeSwitch current="pro" />
        <AuthMenu />
        <CollabIndicator status={collabStatus} peers={collabPeers} onClick={onCollabClick} />
        <CommandPaletteHeaderButton onOpen={onCommandPaletteOpen} />
      </div>
      <StatusStrip
        engineState={engineState}
        playing={playing}
        loadedCount={loadedCount}
        maxDur={maxDur}
        recordingIdx={recordingIdx}
        tempo={tempo}
        musicalKey={musicalKey}
        dryMode={dryMode}
        metronomeOn={metronomeOn}
        onEngineClick={onEngineClick}
        onTempoChange={onTempoChange}
        onKeyChange={onKeyChange}
        onLoadedClick={onLoadedClick}
        onLengthClick={onLengthClick}
        onRecClick={onRecClick}
        onToggleDryMode={onToggleDryMode}
        onToggleMetronome={onToggleMetronome}
        clipCount={clipCount}
        onClipPillClick={onClipPillClick}
      />
    </motion.header>
  );
}
